import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { API_BASE } from "@/lib/api";
import { useWishlist } from "@/hooks/useWishlist";
import Spinner from "@/components/Spinner";
import AdBanner from "@/components/AdBanner";
import type { AppDetail } from "@/types/api";

interface WishlistGame {
  appid: number;
  app: AppDetail;
}

export default function WishlistPage() {
  const { t } = useLang();
  const { list, toggle, count } = useWishlist();
  const [games, setGames] = useState<WishlistGame[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (list.length === 0) { setGames([]); setLoading(false); return; }
    (async () => {
      setLoading(true);
      const results: WishlistGame[] = [];
      // 5개씩 병렬
      for (let i = 0; i < list.length; i += 5) {
        const chunk = list.slice(i, i + 5);
        const settled = await Promise.allSettled(
          chunk.map(async (appid) => {
            const res = await fetch(`${API_BASE}/store/appdetails?appids=${appid}&lang=ko`);
            const data = await res.json();
            if (data[appid]?.success) return { appid, app: data[appid].data as AppDetail };
            return null;
          })
        );
        for (const r of settled) {
          if (r.status === "fulfilled" && r.value) results.push(r.value);
        }
      }
      setGames(results);
      setLoading(false);
    })();
  }, [list]);

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-6 flex-1 w-full">
      {/* Header card */}
      <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4 sm:p-5 mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
          </div>
          <h1 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">{t.nav?.wishlist || "관심 목록"}</h1>
          <span className="text-xs text-gray-400 dark:text-muted ml-1">{count}개</span>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-muted leading-relaxed">{t.wishlist?.description || "관심 목록에 추가한 게임들을 확인할 수 있어요."}</p>
      </div>

      {loading ? <Spinner /> : games.length === 0 ? (
        <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-hover-bg dark:bg-background flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
          </div>
          <p className="text-gray-500 dark:text-muted text-sm mb-1">{t.wishlist?.empty || "관심 목록이 비어있어요"}</p>
          <p className="text-gray-400 dark:text-muted text-xs">게임 상세 페이지에서 하트 버튼을 눌러 추가할 수 있어요.</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {games.map((g) => {
            const p = g.app.price_overview;
            const hasDiscount = p && p.discount_percent > 0;
            return (
              <div key={g.appid} className="bg-card-bg dark:bg-card-bg rounded-xl border border-gray-200 dark:border-card-border overflow-hidden hover:shadow-md hover:border-gray-300 dark:hover:border-card-border transition-all duration-200 group">
                <Link to={`/game/${g.appid}`} className="block">
                  <div className="relative aspect-[46/21.5] bg-hover-bg dark:bg-background overflow-hidden">
                    <img src={g.app.header_image} alt={g.app.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    {hasDiscount && (
                      <div className="absolute top-2 left-2 bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-md">-{p.discount_percent}%</div>
                    )}
                  </div>
                </Link>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <Link to={`/game/${g.appid}`} className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-blue-600 transition-colors">{g.app.name}</h3>
                    </Link>
                    <button onClick={() => toggle(g.appid)} className="w-8 h-8 flex items-center justify-center rounded-lg transition-all bg-red-50 dark:bg-red-500/10 text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20" title="관심 목록에서 제거">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
                    </button>
                  </div>
                  {/* Genres */}
                  <div className="flex flex-wrap gap-1 mb-2">
                    {g.app.genres?.slice(0, 3).map((genre) => (
                      <span key={genre.id} className="text-[10px] px-1.5 py-0.5 bg-hover-bg dark:bg-background text-gray-500 dark:text-muted rounded">{genre.description}</span>
                    ))}
                  </div>
                  {/* Date + Price */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-400 dark:text-muted">{g.app.release_date?.date || ""}</div>
                    <div className="text-right">
                      <div className="flex items-center gap-1.5">
                        {hasDiscount && <span className="text-xs text-gray-400 dark:text-muted line-through">{p.initial_formatted}</span>}
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {g.app.is_free ? "무료" : p?.final_formatted || ""}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}
      <AdBanner className="my-6" />
    </div>
  );
}
